import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, formatConst, LinkedService, Metamodel, Owned, storeConst, SnowflakeTableDataset } from 'shell-models-lib';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { LoggerService } from 'app/logger/logger.service';

export class SnowflakeDatasetTypeHandler extends DatasetTypeHandler {
    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('SnowflakeSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as SnowflakeSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as SnowflakeSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.schemaName.expression = !incomingSourceDatasetsSettings.schemaName ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
        outputSourceDatasetsSettings.tableName.expression = !incomingSourceDatasetsSettings.tableName ? undefined : incomingSourceDatasetsSettings.tableName.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const srcName = elem.entityName.replace(/\[|\]/g, '');
            const tableName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            tableName.expression = srcName.includes('.') ? srcName.split('.')[1] : srcName;
            const schemaName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            schemaName.expression = srcName.includes('.') ? srcName.split('.')[0] : srcName;
            tables.push({
                // need a valid mapping dataflow source name here to pass dataflow validations
                name: getValidTransformationName(true, elem.entityName),
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                sourceDataset: elem.dataset,
                datasetSettings: undefined,
                wildcardPaths: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dslConnectorProperties: new Map<string, any>([
                    [ DslConnectorConstants.schemaName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(schemaName.expression)],
                    [ DslConnectorConstants.tableName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(tableName.expression)]
                ]),
                datasetType: DatasetFormatType.Snowflake,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: srcName
            });
        });

        return tables;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        if (selectedRows.length > 0) {
            return [false, ''];
        }
        return [true, ''];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);

        // Create and populate snowflakeSettings
        const snowflakeSettings = Owned.create<SnowflakeSettings>(SnowflakeSettings.MODEL, table as DataFlowSource);
        snowflakeSettings.initialize(undefined);
        snowflakeSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;
        snowflakeSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
        return snowflakeSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const dataset = new SnowflakeTableDataset('Snowflake');
        dataset.linkedService = linkedService;
        dataset.schema = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.schemaName);
        dataset.table = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        dataset.tableName = dataset.schema + '.' + dataset.table;
        return dataset;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async fetchEntities(loggerService: LoggerService, queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService): Promise<any[]> {
        const list = await queryApiFactoryService.getWizardQueryApi().getNavTableAsync(linkedService, undefined, undefined);
        return list;
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);

        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, formatConst.SQL);
        commonDslConnectorProperties.set(DslConnectorConstants.store, storeConst.Snowflake);
        commonDslConnectorProperties.set('enableNativeCdc', true);
        commonDslConnectorProperties.set('netChanges', true);
        commonDslConnectorProperties.set('systemColumns', false);
        commonDslConnectorProperties.set('skipInitialLoad', true);
    }
}
